import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {((event: Event) => void) | null} */
  #triggerClickCallback = null;
  /** @type {((event: Event) => void) | null} */
  #drawerClickCallback = null;
  /** @type {(() => void) | null} */
  #cardMouseLeaveCallback = null;
  /** @type {((event: Event) => void) | null} */
  #documentClickCallback = null;
  /** @type {((event: Event) => void) | null} */
  #cartUpdateCallback = null;
  /** @type {(() => void) | null} */
  #selectChangeCallback = null;

  connectedCallback() {
    super.connectedCallback();

    const hideBackdrop = () => {
      const backdrop = /** @type {HTMLElement | null} */ (document.querySelector('.quick-add-mobile-backdrop'));
      if (backdrop) {
        backdrop.style.opacity = '0';
        backdrop.style.pointerEvents = 'none';
        setTimeout(() => {
          backdrop.style.display = 'none';
        }, 300);
      }
    };

    // Set up drawer interactions
    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('[data-trigger]'));
    const drawer = /** @type {HTMLElement | null} */ (this.querySelector('[data-drawer]'));
    
    if (trigger && drawer) {
      const onTriggerClick = (/** @type {Event} */ event) => {
        event.preventDefault();
        event.stopPropagation();
        
        // Close all other quick add drawers on the page first
        document.querySelectorAll('.quick-add-drawer-container.is-active').forEach(otherDrawer => {
          if (otherDrawer !== drawer && otherDrawer instanceof HTMLElement) {
            otherDrawer.classList.remove('is-active');
            
            // Show triggers of other drawers
            const otherWrapper = otherDrawer.closest('.quick-add-drawer-wrapper') || document.querySelector(`quick-add-component[data-product-id="${otherDrawer.dataset.productId}"]`);
            if (otherWrapper) {
              const otherTrigger = /** @type {HTMLElement | null} */ (otherWrapper.querySelector('[data-trigger]'));
              if (otherTrigger) otherTrigger.style.display = '';
            }

            // Restore from body teleportation
            if (otherDrawer.classList.contains('is-teleported')) {
              const otherComp = document.querySelector(`quick-add-component[data-product-id="${otherDrawer.dataset.productId}"]`);
              if (otherComp) {
                otherComp.appendChild(otherDrawer);
                otherDrawer.classList.remove('is-teleported');
              }
            }
          }
        });

        // Teleport to body on mobile/tablet viewports to escape parent overflow/transform scope
        if (window.innerWidth < 990) {
          document.body.appendChild(drawer);
          drawer.classList.add('is-teleported');

          // Show backdrop
          let backdrop = /** @type {HTMLElement | null} */ (document.querySelector('.quick-add-mobile-backdrop'));
          if (!backdrop) {
            backdrop = document.createElement('div');
            backdrop.className = 'quick-add-mobile-backdrop';
            backdrop.style.cssText = `
              position: fixed;
              inset: 0;
              background-color: rgba(0, 0, 0, 0.4);
              z-index: 9998;
              opacity: 0;
              transition: opacity 0.3s ease;
              pointer-events: auto !important;
            `;
            document.body.appendChild(backdrop);
          }
          backdrop.style.display = 'block';
          backdrop.style.pointerEvents = 'auto';
          // Force reflow
          backdrop.offsetHeight;
          backdrop.style.opacity = '1';

          // Backdrop click closes the drawer
          const onBackdropClick = (/** @type {Event} */ e) => {
            e.preventDefault();
            e.stopPropagation();
            drawer.classList.remove('is-active');
            if (trigger) trigger.style.display = '';
            drawer.querySelectorAll('[data-dropdown-list]').forEach(menu => menu.classList.remove('is-open'));
            drawer.querySelectorAll('[aria-expanded="true"]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
            this.appendChild(drawer);
            drawer.classList.remove('is-teleported');
            hideBackdrop();
          };
          
          // Clean up old listener using clone
          backdrop.replaceWith(backdrop.cloneNode(true));
          const newBackdrop = /** @type {HTMLElement} */ (document.querySelector('.quick-add-mobile-backdrop'));
          newBackdrop.addEventListener('click', onBackdropClick);
        }

        drawer.classList.add('is-active');
        if (trigger) trigger.style.display = 'none'; // Hide the plus button when drawer is open
      };
      this.#triggerClickCallback = onTriggerClick;
      trigger.addEventListener('click', onTriggerClick);

      // Mobile trigger support
      const parentCard = /** @type {HTMLElement | null} */ (this.closest('product-card') || this.closest('.best-sellers-card') || this.closest('.product-grid__item'));
      const mobileTrigger = parentCard ? /** @type {HTMLElement | null} */ (parentCard.querySelector('[data-mobile-trigger]')) : null;
      if (mobileTrigger) {
        mobileTrigger.addEventListener('click', onTriggerClick);
      }

      // Close button inside the drawer
      const closeBtn = /** @type {HTMLElement | null} */ (drawer.querySelector('[data-drawer-close]'));
      if (closeBtn) {
        closeBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          drawer.classList.remove('is-active');
          if (trigger) trigger.style.display = '';

          // Close custom dropdowns
          drawer.querySelectorAll('[data-dropdown-list]').forEach(menu => menu.classList.remove('is-open'));
          drawer.querySelectorAll('[aria-expanded="true"]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));

          // Restore to parent component if teleported
          if (drawer.classList.contains('is-teleported')) {
            this.appendChild(drawer);
            drawer.classList.remove('is-teleported');
          }
          hideBackdrop();
        });
      }

      // Stop click propagation on the drawer itself to prevent clicks from navigating the parent card link
      const onDrawerClick = (/** @type {Event} */ event) => {
        event.stopPropagation();
        
        // If click is not on custom trigger or option, close all custom menus
        const target = /** @type {Element | null} */ (event.target);
        if (target && !target.closest('[data-select-trigger]') && !target.closest('[data-qty-trigger]') && !target.closest('[data-dropdown-list]')) {
          drawer.querySelectorAll('[data-dropdown-list]').forEach(menu => menu.classList.remove('is-open'));
          drawer.querySelectorAll('[aria-expanded="true"]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
        }
      };
      this.#drawerClickCallback = onDrawerClick;
      drawer.addEventListener('click', onDrawerClick);

      // Close drawer on mouseleave of the parent card
      if (parentCard) {
        const onMouseLeave = () => {
          // Do not close on mouseleave if mobile view or teleported
          if (window.innerWidth < 990 || drawer.classList.contains('is-teleported')) return;

          drawer.classList.remove('is-active');
          if (trigger) trigger.style.display = '';
          // Close custom dropdowns
          drawer.querySelectorAll('[data-dropdown-list]').forEach(menu => menu.classList.remove('is-open'));
          drawer.querySelectorAll('[aria-expanded="true"]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));

          // Restore to parent component if teleported
          if (drawer.classList.contains('is-teleported')) {
            this.appendChild(drawer);
            drawer.classList.remove('is-teleported');
          }
          hideBackdrop();
        };
        this.#cardMouseLeaveCallback = onMouseLeave;
        parentCard.addEventListener('mouseleave', onMouseLeave);
      }

      // Close drawer on click outside
      const onDocumentClick = (/** @type {Event} */ event) => {
        const target = /** @type {Node | null} */ (event.target);
        if (target && !this.contains(target) && !drawer.contains(target)) {
          drawer.classList.remove('is-active');
          if (trigger) trigger.style.display = '';
          // Close custom dropdowns
          drawer.querySelectorAll('[data-dropdown-list]').forEach(menu => menu.classList.remove('is-open'));
          drawer.querySelectorAll('[aria-expanded="true"]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));

          // Restore to parent component if teleported
          if (drawer.classList.contains('is-teleported')) {
            this.appendChild(drawer);
            drawer.classList.remove('is-teleported');
          }
          hideBackdrop();
        }
      };
      this.#documentClickCallback = onDocumentClick;
      document.addEventListener('click', onDocumentClick);
    }

    // Set up custom dropdown lists
    const dropdownFields = drawer ? drawer.querySelectorAll('[data-dropdown-field]') : this.querySelectorAll('[data-dropdown-field]');
    dropdownFields.forEach(field => {
      const triggerBtn = /** @type {HTMLElement | null} */ (field.querySelector('[data-select-trigger]') || field.querySelector('[data-qty-trigger]'));
      const menu = /** @type {HTMLElement | null} */ (field.querySelector('[data-dropdown-list]'));
      const displayVal = /** @type {HTMLElement | null} */ (field.querySelector('[data-select-value]') || field.querySelector('[data-qty-value]'));
      const nativeSelect = /** @type {HTMLSelectElement | null} */ (field.querySelector('select'));

      if (triggerBtn && menu && displayVal && nativeSelect) {
        triggerBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          console.log('Quick Add: Custom trigger clicked for', nativeSelect.name);
          
          // Close other open custom dropdowns in this quick add card
          if (drawer) {
            drawer.querySelectorAll('[data-dropdown-list]').forEach(otherMenu => {
              if (otherMenu !== menu) {
                otherMenu.classList.remove('is-open');
                const otherTrigger = /** @type {HTMLElement | null} */ (otherMenu.previousElementSibling);
                if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
              }
            });
          }
 
          const isOpen = menu.classList.toggle('is-open');
          console.log('Quick Add: Dropdown menu is-open state:', isOpen);
          triggerBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });

        const options = menu.querySelectorAll('[data-option-value]');
        options.forEach(opt => {
          opt.addEventListener('click', (event) => {
            event.stopPropagation();
            const val = opt.getAttribute('data-option-value') || '';
            console.log('Quick Add: Option selected:', val);
            displayVal.textContent = val;

            // Set native select and trigger variant check
            nativeSelect.value = val;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));

            // Update custom selected attributes
            options.forEach(o => o.removeAttribute('data-selected'));
            opt.setAttribute('data-selected', 'true');

            // Close menu
            menu.classList.remove('is-open');
            triggerBtn.setAttribute('aria-expanded', 'false');
          });
        });
      }
    });

    // Handle variant selection change
    const optionSelects = drawer ? /** @type {NodeListOf<HTMLSelectElement>} */ (drawer.querySelectorAll('[data-option-select]')) : /** @type {NodeListOf<HTMLSelectElement>} */ (this.querySelectorAll('[data-option-select]'));
    const variantIdInput = drawer ? /** @type {HTMLInputElement | null} */ (drawer.querySelector('[data-variant-id-input]')) : /** @type {HTMLInputElement | null} */ (this.querySelector('[data-variant-id-input]'));
    const variantsJsonEl = drawer ? drawer.querySelector('[data-variants-json]') : this.querySelector('[data-variants-json]');
    
    if (optionSelects.length > 0 && variantIdInput && variantsJsonEl) {
      /** @type {Array<{id: number, options: string[], available: boolean}>} */
      const variants = JSON.parse(variantsJsonEl.textContent || '[]');

      const onSelectChange = () => {
        const selectedValues = Array.from(optionSelects).map(select => select.value);
        const matchingVariant = variants.find(variant => {
          return variant.options.every((option, index) => option === selectedValues[index]);
        });

        if (matchingVariant) {
          variantIdInput.value = matchingVariant.id.toString();
          variantIdInput.disabled = !matchingVariant.available;

          // Update the add to cart submit button status
          const submitBtn = drawer ? /** @type {HTMLButtonElement | null} */ (drawer.querySelector('[data-submit-btn]')) : /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-submit-btn]'));
          if (submitBtn) {
            submitBtn.disabled = !matchingVariant.available;
            if (!matchingVariant.available) {
              submitBtn.classList.add('disabled');
            } else {
              submitBtn.classList.remove('disabled');
            }
          }
        }
      };
      this.#selectChangeCallback = onSelectChange;

      optionSelects.forEach(select => {
        select.addEventListener('change', onSelectChange);
      });
    }

    // Listen for form submission to trigger the fly-to-cart animation
    const form = drawer ? drawer.querySelector('form') : this.querySelector('form');
    if (form) {
      form.addEventListener('submit', () => {
        const submitBtn = drawer ? drawer.querySelector('[data-submit-btn]') : this.querySelector('[data-submit-btn]');
        const cartIcon = document.querySelector('.header-actions__cart-icon');
        const image = this.dataset.productImage;

        if (submitBtn && cartIcon && image) {
          const flyToCartElement = /** @type {any} */ (document.createElement('fly-to-cart'));
          flyToCartElement.style.setProperty('background-image', `url(${image})`);
          flyToCartElement.source = submitBtn;
          flyToCartElement.destination = cartIcon;
          document.body.appendChild(flyToCartElement);
        }
      });
    }

    // Auto-open the cart drawer when item is successfully added to cart via quick add
    const onCartUpdate = (/** @type {Event} */ event) => {
      const customEvent = /** @type {CustomEvent} */ (event);
      if (customEvent.detail && customEvent.detail.data && !customEvent.detail.data.didError) {
        if (drawer && drawer.classList.contains('is-active')) {
          // Close the drawer if open and restore teleportation
          drawer.classList.remove('is-active');
          if (trigger) trigger.style.display = '';
          if (drawer.classList.contains('is-teleported')) {
            this.appendChild(drawer);
            drawer.classList.remove('is-teleported');
          }
          hideBackdrop();

          setTimeout(() => {
            const cartDrawer = /** @type {HTMLElement & { open?: () => void } | null} */ (
              document.querySelector('cart-drawer-component')
            );
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          }, 500); // 500ms delay to let the fly-to-cart animation complete/land
        }
      }
    };
    this.#cartUpdateCallback = onCartUpdate;
    window.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#cartUpdateCallback) {
      window.removeEventListener(ThemeEvents.cartUpdate, this.#cartUpdateCallback);
    }

    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('[data-trigger]'));
    if (trigger && this.#triggerClickCallback) {
      trigger.removeEventListener('click', this.#triggerClickCallback);
    }

    const parentCard = /** @type {HTMLElement | null} */ (this.closest('product-card') || this.closest('.best-sellers-card') || this.closest('.product-grid__item'));
    const mobileTrigger = parentCard ? /** @type {HTMLElement | null} */ (parentCard.querySelector('[data-mobile-trigger]')) : null;
    if (mobileTrigger && this.#triggerClickCallback) {
      mobileTrigger.removeEventListener('click', this.#triggerClickCallback);
    }

    const drawer = /** @type {HTMLElement | null} */ (this.querySelector('[data-drawer]'));
    if (drawer && this.#drawerClickCallback) {
      drawer.removeEventListener('click', this.#drawerClickCallback);
    }

    if (parentCard && this.#cardMouseLeaveCallback) {
      parentCard.removeEventListener('mouseleave', this.#cardMouseLeaveCallback);
    }

    if (this.#documentClickCallback) {
      document.removeEventListener('click', this.#documentClickCallback);
    }

    if (this.#selectChangeCallback) {
      const optionSelects = /** @type {NodeListOf<HTMLSelectElement>} */ (this.querySelectorAll('[data-option-select]'));
      optionSelects.forEach(select => {
        if (this.#selectChangeCallback) {
          select.removeEventListener('change', this.#selectChangeCallback);
        }
      });
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
